package er.prateek.crossly.ui.streak

import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.graphics.luminance
import androidx.compose.ui.unit.dp
import crossly.shared.generated.resources.Res
import crossly.shared.generated.resources.streak_at_risk
import crossly.shared.generated.resources.streak_best
import crossly.shared.generated.resources.streak_broken
import crossly.shared.generated.resources.streak_day_fri
import crossly.shared.generated.resources.streak_day_mon
import crossly.shared.generated.resources.streak_day_sat
import crossly.shared.generated.resources.streak_day_sun
import crossly.shared.generated.resources.streak_day_thu
import crossly.shared.generated.resources.streak_day_tue
import crossly.shared.generated.resources.streak_day_wed
import crossly.shared.generated.resources.streak_frozen_hint
import crossly.shared.generated.resources.streak_played_today
import crossly.shared.generated.resources.streak_restore_cta
import crossly.shared.generated.resources.streak_share
import crossly.shared.generated.resources.streak_start
import crossly.shared.generated.resources.streak_title
import crossly.shared.generated.resources.streak_title_zero
import er.prateek.crossly.di.AppContainer
import er.prateek.crossly.progression.StreakDay
import er.prateek.crossly.ui.components.CenteredRow
import er.prateek.crossly.ui.components.GameButton
import er.prateek.crossly.ui.components.GameButtonSize
import er.prateek.crossly.ui.components.GameButtonStyle
import er.prateek.crossly.ui.components.GameCard
import er.prateek.crossly.ui.components.IconCheck
import er.prateek.crossly.ui.components.IconFlame
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game
import kotlinx.datetime.DayOfWeek
import org.jetbrains.compose.resources.stringResource

/**
 * The "frozen" tint for a lost or restored day. A fixed ice blue rather than a palette colour:
 * every palette is warm, and a warm "frozen" state just reads as a faded flame.
 */
@Composable
fun iceColor(): Color = if (MaterialTheme.game.card.luminance() < 0.5f) Color(0xFF7CC8F5) else Color(0xFF3A9FDB)

/**
 * Home's streak panel: the flame, the count, a one-line "what to do now", the last seven days,
 * and -- when the streak froze -- the way to buy it back. The whole streak loop lives here so
 * a player can read their status at a glance without opening anything.
 */
@Composable
fun StreakCard(
    onShare: () -> Unit,
    onRestore: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val colors = MaterialTheme.game
    val manager = AppContainer.streakManager
    // Read through the state so this recomposes whenever the streak changes.
    val stored = manager.streak
    val display = manager.displayStreak()
    val playedToday = manager.playedToday()
    val offer = manager.restoreOffer()
    val frozen = offer != null
    val alive = display > 0

    val transition = rememberInfiniteTransition(label = "streakCard")
    val pulse by transition.animateFloat(
        initialValue = 1f,
        targetValue = if (alive && !playedToday) 1.12f else 1.04f,
        animationSpec = infiniteRepeatable(tween(900), RepeatMode.Reverse),
        label = "flamePulse",
    )

    GameCard(
        modifier = modifier.fillMaxWidth(),
        accentGlow = when {
            frozen -> iceColor().copy(alpha = 0.7f)
            alive -> colors.flame.copy(alpha = 0.6f)
            else -> null
        },
    ) {
        CenteredRow(modifier = Modifier.fillMaxWidth(), spacing = Spacing.md) {
            Box(
                modifier = Modifier
                    .size(60.dp)
                    .background(
                        (if (frozen) iceColor() else if (alive) colors.flame else colors.locked).copy(alpha = 0.16f),
                        CircleShape,
                    ),
                contentAlignment = Alignment.Center,
            ) {
                IconFlame(
                    size = 40.dp,
                    tint = when {
                        frozen -> iceColor()
                        alive -> colors.flame
                        else -> colors.locked
                    },
                    coreTint = if (alive) colors.gold else null,
                    modifier = Modifier.graphicsLayer { scaleX = pulse; scaleY = pulse },
                )
            }
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = when {
                        frozen -> stringResource(Res.string.streak_broken, offer.lostStreak)
                        alive -> stringResource(Res.string.streak_title, display)
                        else -> stringResource(Res.string.streak_title_zero)
                    },
                    style = MaterialTheme.typography.titleLarge,
                    color = colors.ink,
                )
                Text(
                    text = when {
                        frozen -> stringResource(Res.string.streak_frozen_hint)
                        playedToday -> stringResource(Res.string.streak_played_today)
                        alive -> stringResource(Res.string.streak_at_risk)
                        else -> stringResource(Res.string.streak_start)
                    },
                    style = MaterialTheme.typography.bodySmall,
                    color = if (frozen || (alive && !playedToday)) colors.flame else colors.inkMuted,
                )
            }
        }

        WeekStrip(
            days = manager.recentDays(7),
            modifier = Modifier.fillMaxWidth().padding(top = Spacing.lg),
        )

        CenteredRow(modifier = Modifier.fillMaxWidth().padding(top = Spacing.lg), spacing = Spacing.sm) {
            Text(
                text = stringResource(Res.string.streak_best, maxOf(stored.longest, display)),
                style = MaterialTheme.typography.labelLarge,
                color = colors.inkMuted,
                modifier = Modifier.weight(1f),
            )
            if (alive) {
                GameButton(
                    text = stringResource(Res.string.streak_share),
                    onClick = onShare,
                    style = GameButtonStyle.SECONDARY,
                    size = GameButtonSize.SMALL,
                )
            }
        }
        if (frozen) {
            GameButton(
                text = stringResource(Res.string.streak_restore_cta),
                onClick = onRestore,
                style = GameButtonStyle.GOLD,
                fillWidth = true,
                modifier = Modifier.padding(top = Spacing.md),
                icon = { tint -> IconFlame(size = 18.dp, tint = tint) },
            )
        }
    }
}

@Composable
private fun WeekStrip(days: List<StreakDay>, modifier: Modifier = Modifier) {
    Row(modifier = modifier, horizontalArrangement = Arrangement.SpaceBetween) {
        days.forEach { day -> DayDot(day) }
    }
}

@Composable
private fun DayDot(day: StreakDay) {
    val colors = MaterialTheme.game
    val ice = iceColor()
    Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(Spacing.xs)) {
        val base = Modifier.size(34.dp)
        Box(
            modifier = when {
                day.played -> base.background(colors.flame, CircleShape)
                day.restored -> base.background(ice.copy(alpha = 0.3f), CircleShape).border(2.dp, ice, CircleShape)
                day.isToday -> base.border(2.dp, colors.flame.copy(alpha = 0.8f), CircleShape)
                else -> base.background(colors.locked.copy(alpha = 0.18f), CircleShape)
            },
            contentAlignment = Alignment.Center,
        ) {
            when {
                day.played -> IconFlame(size = 18.dp, tint = colors.onAccent, coreTint = colors.gold)
                day.restored -> IconCheck(size = 14.dp, tint = ice)
                else -> Unit
            }
        }
        Text(
            text = stringResource(day.date.dayOfWeek.initialRes()),
            style = MaterialTheme.typography.labelSmall,
            color = if (day.isToday) colors.ink else colors.inkMuted,
        )
    }
}

private fun DayOfWeek.initialRes() = when (this) {
    DayOfWeek.MONDAY -> Res.string.streak_day_mon
    DayOfWeek.TUESDAY -> Res.string.streak_day_tue
    DayOfWeek.WEDNESDAY -> Res.string.streak_day_wed
    DayOfWeek.THURSDAY -> Res.string.streak_day_thu
    DayOfWeek.FRIDAY -> Res.string.streak_day_fri
    DayOfWeek.SATURDAY -> Res.string.streak_day_sat
    DayOfWeek.SUNDAY -> Res.string.streak_day_sun
}
